import { ICharacter } from "@next-platform/client-common-core";
import React from "react";

import { CharacterItemAction, DataSourceType, DisplayContext } from "../types";
import { MyChatbotsHits, SelectableHits, SharedChatbotsHits } from "./components";
import resolveShouldFilterNsfwCharactersForDataSource from "./utils/resolveShouldFilterNsfwCharactersForDataSource";

interface ICharacterHitsProps {
  context: DisplayContext;

  // MEMBER_SELECT specific props
  onItemAction?: (action: CharacterItemAction, character: ICharacter | undefined) => void;
  selectedItems?: string[];
  maxItemsToSelect?: number;
  onLimitReached?: (currentCount: number, maxCount: number) => void;

  // Common props
  isPartner: boolean;
  nsfwToggle: boolean;
  recommId: string;
  currentCategory: string;
  shouldFilterNsfwCharacters?: boolean;

  // Data source indicator
  dataSourceType?: DataSourceType;
}

const CharacterHitsV2 = React.memo((props: ICharacterHitsProps) => {
  const {
    context,
    isPartner,
    nsfwToggle,
    recommId,
    currentCategory,
    shouldFilterNsfwCharacters,
    onItemAction,
    selectedItems = [],
    maxItemsToSelect,
    onLimitReached,
    dataSourceType = DataSourceType.SHARED_CHATBOTS,
  } = props;
  const shouldFilterNsfwCharactersForDataSource = resolveShouldFilterNsfwCharactersForDataSource({
    shouldFilterNsfwCharacters,
    dataSourceType,
  });

  // Render based on context
  switch (context) {
    case DisplayContext.MEMBER_SELECT: {
      const emptyMessage =
        dataSourceType === DataSourceType.MY_CHATBOTS
          ? "Try clearing your filters or use another keyword or create a chatbot to use in group."
          : null; // Default empty message for other options

      return (
        <SelectableHits
          onItemAction={onItemAction}
          selectedItems={selectedItems}
          maxItemsToSelect={maxItemsToSelect}
          onLimitReached={onLimitReached}
          isPartner={isPartner}
          dataSourceType={dataSourceType}
          emptyStateDescription={emptyMessage}
          shouldFilterNsfwCharacters={shouldFilterNsfwCharactersForDataSource}
        />
      );
    }
    case DisplayContext.MY_CHATBOTS:
      return <MyChatbotsHits nsfwToggle={nsfwToggle} isPartner={isPartner} currentCategory={currentCategory} />;

    case DisplayContext.FAVORITES:
    case DisplayContext.SHARED_CHATBOTS:
    default:
      return (
        <SharedChatbotsHits
          context={context}
          nsfwToggle={nsfwToggle}
          isPartner={isPartner}
          currentCategory={currentCategory}
          recommId={recommId}
          shouldFilterNsfwCharacters={shouldFilterNsfwCharactersForDataSource}
        />
      );
  }
});

CharacterHitsV2.displayName = "CharacterHitsV2";

export default CharacterHitsV2;
